// Mobile grouping of the sidebar's modules. Everything (labels, routes,
// icons, sub-menus, who sees them) comes from the desktop sidebar's config
// via useVisibleNav(): each visible department (navConfig DEPARTMENTS) is a
// mobile section with the same modules in the same order, and the top-level
// modules outside the departments are grouped by the GENERAL sections below.
// A top-level module that isn't mapped here lands in "Other", so nothing
// goes missing when new modules are added to the sidebar.
import { CalendarClock, LayoutDashboard, LayoutGrid, Users } from "lucide-react";
import {
  PRODUCTION_FLOW_NAV, CRM_NAV,
} from "@/components/layout/navConfig";

const GENERAL_SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, paths: ["/", "/ceo-dashboard"] },
  // "/hr/attendance/admin": HR's entry for a restricted account (navConfig USER_VIEW_RESTRICTIONS).
  { key: "hr", label: "HR", icon: Users, paths: ["/hr", "/hr/attendance/admin"] },
];
const DAILY_REPORTS = { key: "daily-reports", label: "Daily Reports", short: "Daily", icon: CalendarClock, paths: ["/daily-reports"] };
const OTHER = { key: "other", label: "Other", icon: LayoutGrid, paths: [] };
// Departments with a long name get a short chip label.
const SHORT = { design: "Design", "production-planning": "Production", inventory: "Inventory" };

// Sidebar entries that open a sub-menu instead of a page (same children,
// same per-role filter as the desktop sidebar).
function childrenFor(to) {
  switch (to) {
    case "/production-flow": return PRODUCTION_FLOW_NAV;
    case "/crm": return CRM_NAV;
    default: return null;
  }
}

// Everything this user's desktop sidebar shows, as mobile sections, in
// sidebar order: top-level modules, then one section per department, then
// Daily Reports (written inline in the desktop sidebar, mirrored here).
export function buildMobileNav({ visibleGeneralNav, visibleDepartments, subAllowed = () => true, canSeeDailyReports = true }) {
  const withChildren = (n) => ({ ...n, children: childrenFor(n.to)?.filter((c) => subAllowed(c.to)) || null });
  const general = visibleGeneralNav.map(withChildren);
  const groupOf = (to) => GENERAL_SECTIONS.find((s) => s.paths.includes(to)) || OTHER;
  const groups = [
    ...[...GENERAL_SECTIONS, OTHER].map((s) => ({ ...s, modules: general.filter((m) => groupOf(m.to).key === s.key) })),
    ...visibleDepartments.map((d) => ({ key: d.key, label: d.label, short: SHORT[d.key], icon: d.icon, modules: d.items.map(withChildren) })),
    { ...DAILY_REPORTS, modules: canSeeDailyReports ? [{ to: "/daily-reports", label: "Daily Reports", icon: CalendarClock, tid: "nav-daily-reports" }] : [] },
  ].filter((g) => g.modules.length > 0);
  const modules = groups.flatMap((g) => g.modules);
  const sectionOf = (to) => groups.find((g) => g.modules.some((m) => m.to === to)) || OTHER;
  return { modules, groups, sectionOf };
}

// The module (and child page) the current URL belongs to, for the
// breadcrumb. Longest matching route wins.
export function findLocation(modules, pathname) {
  const hit = (to) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/"));
  let best = null;
  for (const m of modules) {
    for (const c of m.children || []) {
      if (hit(c.to) && (!best || c.to.length > best.score)) best = { module: m, child: c.to === m.to ? null : c, score: c.to.length };
    }
    if (hit(m.to) && (!best || m.to.length > best.score)) best = { module: m, child: null, score: m.to.length };
  }
  return best;
}
